import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { paymentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { Check, Zap, CreditCard, Wallet } from 'lucide-react';
import { motion } from 'framer-motion';

const PACKAGES = [
  {
    id: 'starter',
    name: 'STARTER',
    credits: 10,
    price: 9.99,
    features: ['10 Agent Credits', 'All Active Agents', 'Basic LLM (GPT-5.2)', 'Email Support'],
    popular: false,
  },
  {
    id: 'professional',
    name: 'PROFESSIONAL',
    credits: 50,
    price: 39.99,
    features: ['50 Agent Credits', 'All Active Agents', 'Multi-LLM Support', 'Custom API Keys', 'Priority Support'],
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'ENTERPRISE',
    credits: 200,
    price: 129.99,
    features: ['200 Agent Credits', 'All Agents (incl. Premium)', 'Multi-LLM Support', 'Custom API Keys', 'Execution History', 'Dedicated Support', 'API Access'],
    popular: false,
  },
];

const PAYMENT_ICONS = {
  stripe: CreditCard,
  paypal: Wallet,
  razorpay: CreditCard,
};

const PAYMENT_LABELS = {
  stripe: 'Card (Stripe)',
  paypal: 'PayPal',
  razorpay: 'Razorpay',
};

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [methods, setMethods] = useState({});
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    paymentsAPI.methods().then(res => setMethods(res.data.methods || {})).catch(() => {});
  }, []);

  const handlePurchase = async (packageId) => {
    if (!user) { navigate('/auth'); return; }
    setLoading(packageId);
    try {
      const res = await paymentsAPI.checkout({
        package_id: packageId,
        origin_url: window.location.origin,
        payment_method: selectedMethod,
      });
      if (selectedMethod === 'razorpay' && res.data.razorpay_order_id) {
        // Razorpay opens a modal on frontend
        const options = {
          key: res.data.razorpay_key_id,
          amount: res.data.amount,
          currency: res.data.currency,
          name: 'Defences.ai',
          description: `Credit Package`,
          order_id: res.data.razorpay_order_id,
          handler: async (response) => {
            await paymentsAPI.razorpayVerify(response);
            navigate(`/payment/success?session_id=${res.data.session_id}`);
          },
          theme: { color: '#FF7A3D' },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Checkout failed';
      alert(detail);
    } finally {
      setLoading(null);
    }
  };

  const availableMethods = Object.entries(methods).filter(([, v]) => v.enabled);
  const hasMultipleMethods = availableMethods.length > 1;

  return (
    <div className="min-h-screen bg-[#020202] grid-bg" data-testid="pricing-page">
      <Navbar />
      <div className="pt-24 px-6 md:px-12 pb-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="font-mono text-xs text-[#FF7A3D] tracking-widest uppercase mb-3" data-testid="pricing-label">PRICING</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
              Fuel Your <span className="text-[#FF7A3D] glow-green">Defences</span>
            </h1>
            <p className="font-body text-lg text-[#bbb] max-w-lg mx-auto">
              Purchase credits to power AI agent executions. No subscriptions. Pay as you go.
            </p>
          </div>

          {/* Payment Method Selector */}
          <div className="flex items-center justify-center gap-3 mb-10" data-testid="payment-method-selector">
            <span className="font-mono text-[10px] text-[#888] tracking-widest uppercase mr-2">PAY WITH</span>
            {Object.entries(PAYMENT_LABELS).map(([key, label]) => {
              const Icon = PAYMENT_ICONS[key];
              const enabled = methods[key]?.enabled;
              const isActive = selectedMethod === key;
              return (
                <button
                  key={key}
                  onClick={() => enabled && setSelectedMethod(key)}
                  data-testid={`method-${key}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-heading uppercase tracking-wider transition-all border ${
                    isActive
                      ? 'bg-[#FF7A3D]/10 border-[#FF7A3D]/40 text-[#FF7A3D]'
                      : enabled
                        ? 'bg-white/[0.02] border-white/10 text-[#999] hover:border-white/20 hover:text-white'
                        : 'bg-white/[0.01] border-white/5 text-[#555] cursor-not-allowed'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                  {!enabled && <span className="text-[8px] text-[#555] ml-1">(SOON)</span>}
                </button>
              );
            })}
          </div>

          {/* Packages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PACKAGES.map((pkg, i) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`card-cyber rounded-sm overflow-hidden relative ${pkg.popular ? 'border-[#FF7A3D]/30' : ''}`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-[#FF7A3D] py-1 text-center">
                    <span className="font-heading text-[10px] font-bold text-black tracking-widest uppercase">MOST POPULAR</span>
                  </div>
                )}
                <div className={`p-8 ${pkg.popular ? 'pt-12' : ''}`}>
                  <h3 className="font-heading text-lg font-bold text-white mb-1">{pkg.name}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="font-heading text-4xl font-bold text-white">${pkg.price}</span>
                    <span className="font-mono text-xs text-[#999]">USD</span>
                  </div>
                  <div className="flex items-center gap-2 mb-6 p-3 bg-[#FF7A3D]/5 border border-[#FF7A3D]/10 rounded-sm">
                    <Zap className="w-4 h-4 text-[#FF7A3D]" />
                    <span className="font-heading text-sm font-bold text-[#FF7A3D]">{pkg.credits} CREDITS</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map(f => (
                      <li key={f} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#FF7A3D] flex-shrink-0" />
                        <span className="font-body text-sm text-[#bbb]">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={loading === pkg.id}
                    className={`w-full rounded-sm h-11 font-heading font-bold uppercase tracking-widest text-xs transition-all ${
                      pkg.popular
                        ? 'bg-[#FF7A3D] text-black hover:bg-[#FF7A3D]/90 hover:shadow-[0_0_15px_rgba(255,122,61,0.4)]'
                        : 'bg-white/5 text-white border border-white/10 hover:border-[#FF7A3D]/30 hover:text-[#FF7A3D]'
                    }`}
                    data-testid={`purchase-${pkg.id}-btn`}
                  >
                    {loading === pkg.id ? 'PROCESSING...' : 'PURCHASE'}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Free tier info */}
          <div className="mt-12 text-center">
            <p className="font-mono text-xs text-[#bbb]">
              New accounts receive 5 free credits to test our agents.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
